/**
 * Two-factor authentication management card for the profile/settings page.
 * Shows current status, lets users add an authenticator, and remove existing
 * ones. Admins are reminded that 2FA is required for their account.
 */
import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, Trash2, Plus, CheckCircle2, AlertCircle } from "lucide-react";
import { listTotpFactors, unenrollTotp, type TotpFactor } from "@/lib/auth/mfa";
import MfaEnroll from "./MfaEnroll";

const GRAD = "#5048E5";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";
const muted = "hsl(var(--muted))";
const surface = "hsl(var(--card))";

export default function MfaCard({ isAdmin }: { isAdmin?: boolean }) {
  const [factors, setFactors] = useState<TotpFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setFactors((await listTotpFactors()).filter((f) => f.status === "verified"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load two-factor status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const enabled = factors.length > 0;

  async function handleRemove(id: string) {
    setRemovingId(id);
    setError("");
    try {
      await unenrollTotp(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove this authenticator.");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
      <h2 className="mb-4 text-sm font-bold uppercase tracking-wide" style={{ fontFamily: "Outfit, sans-serif", color: fgMuted }}>
        Two-Factor Authentication
      </h2>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ background: enabled ? "hsla(143,70%,45%,0.15)" : `linear-gradient(135deg, ${GRAD}, #a855f7)`, color: enabled ? "hsl(143 60% 40%)" : "#fff" }}>
            {enabled ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />}
          </span>
          <div>
            <p className="text-sm font-semibold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              {enabled ? "Two-factor is on" : "Add an authenticator app"}
            </p>
            <p className="text-xs" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              {enabled
                ? "You'll be asked for a code from your authenticator app when you sign in."
                : "Use Google Authenticator, Microsoft Authenticator, Authy or 1Password for an extra layer of security."}
            </p>
            {isAdmin && (
              <p className="mt-1.5 text-xs font-semibold" style={{ color: enabled ? "hsl(143 60% 40%)" : "#ef4444", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                {enabled ? "Required for admin accounts — active." : "Required for admin accounts."}
              </p>
            )}
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-3 flex items-start gap-1.5 text-xs" style={{ color: "#ef4444", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          <AlertCircle size={13} className="mt-px shrink-0" /> {error}
        </p>
      )}

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          <Loader2 size={15} className="animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {factors.length > 0 && (
            <ul className="mt-4 flex flex-col gap-2">
              {factors.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5"
                  style={{ background: muted, border: `1px solid ${border}` }}>
                  <span className="flex items-center gap-2 text-sm" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                    <CheckCircle2 size={14} style={{ color: "hsl(143 60% 40%)" }} />
                    {f.friendly_name || "Authenticator app"}
                  </span>
                  <button type="button" onClick={() => handleRemove(f.id)} disabled={removingId === f.id}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-60"
                    style={{ background: "hsla(0,70%,55%,0.1)", color: "hsl(0 70% 55%)", border: "1px solid hsla(0,70%,55%,0.3)", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                    {removingId === f.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {adding ? (
            <div className="mt-4 rounded-2xl p-4" style={{ background: muted, border: `1px solid ${border}` }}>
              <MfaEnroll
                onVerified={() => { setAdding(false); refresh(); }}
                onCancel={() => setAdding(false)}
              />
            </div>
          ) : (
            <button type="button" onClick={() => { setAdding(true); setError(""); }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold transition-all hover:opacity-90"
              style={{ background: enabled ? muted : GRAD, color: enabled ? fg : "#fff", border: enabled ? `1px solid ${border}` : "none", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              <Plus size={15} /> {enabled ? "Add another authenticator" : "Set up two-factor"}
            </button>
          )}
        </>
      )}
    </div>
  );
}
